import { User } from '../types';

// Characters allowed in a username after the @.
const MENTION_CHARS = 'A-Za-z0-9._-';
const MENTION_TOKEN_RE = new RegExp(`@([${MENTION_CHARS}]+)`);
const MAX_MENTIONS = 5;

interface MentionToken {
  // Position of the @ in the text.
  start: number;
  // Position just after the last character of the token.
  end: number;
  // The text typed after the @.
  query: string;
}

// The @token being typed at the caret, null when the caret is not on one.
const getMentionToken = (text: string, caret: number): MentionToken | null => {
  const before = text.slice(0, caret);
  const match = new RegExp(`(^|\\s)@([${MENTION_CHARS}]*)$`).exec(before);
  if (!match) {
    return null;
  }
  const start = before.length - match[2].length - 1;
  const after = /^[A-Za-z0-9._-]*/.exec(text.slice(caret));
  const end = caret + (after ? after[0].length : 0);
  return { start, end, query: match[2] };
};

// Ids of the picked users whose @username is still in the text (5 at most).
const getMentionIds = (text: string, picked: User[]) => {
  const names: string[] = [];
  const re = new RegExp(MENTION_TOKEN_RE.source, 'g');
  let match = re.exec(text);
  while (match) {
    names.push(match[1].toLowerCase());
    match = re.exec(text);
  }
  const ids: string[] = [];
  picked.forEach((u) => {
    if (names.includes(u.username.toLowerCase()) && !ids.includes(u.id)) {
      ids.push(u.id);
    }
  });
  return ids.slice(0, MAX_MENTIONS);
};

interface MentionSegment {
  text: string;
  user?: User;
}

// Cuts a message into plain text and the @username of the mentioned users
// (every @token when the users are not known, like in a notification).
const splitMentions = (text: string, mentions?: User[], all = false): MentionSegment[] => {
  if (!text || (!all && (!mentions || !mentions.length))) {
    return [{ text }];
  }
  const byName: { [name: string]: User } = {};
  (mentions || []).forEach((u) => {
    if (u && u.username) {
      byName[u.username.toLowerCase()] = u;
    }
  });
  const segments: MentionSegment[] = [];
  let last = 0;
  const re = new RegExp(MENTION_TOKEN_RE.source, 'g');
  let match = re.exec(text);
  while (match) {
    const user = byName[match[1].toLowerCase()] || (all ? { id: '', username: match[1] } as User : undefined);
    if (user) {
      if (match.index > last) {
        segments.push({ text: text.slice(last, match.index) });
      }
      segments.push({ text: match[0], user });
      last = match.index + match[0].length;
    }
    match = re.exec(text);
  }
  if (last < text.length) {
    segments.push({ text: text.slice(last) });
  }
  return segments;
};

export type { MentionSegment, MentionToken };

export {
  MAX_MENTIONS,
  getMentionIds,
  getMentionToken,
  splitMentions,
};
